import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Menu, Home, Grid3X3, Camera, ShoppingCart, User, Settings, Package, LayoutDashboard, BarChart3, LogOut, X, ShieldCheck, Sparkles, Search, Eye } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const searchInputRef = useRef(null);

  // Hide on marketing pages (landing + onboarding) AND on independent shop public pages
  const HIDE_ON = ['/', '/apri-negozio'];
  const isShopPublic = location.pathname.startsWith('/negozio/') && location.pathname !== '/negozio/impostazioni';
  // Also hide on product detail when the user came from a shop (preserves independent shop UX)
  const queryParams = new URLSearchParams(location.search);
  const fromShop = queryParams.get('from') || queryParams.get('shop');
  const isProductFromShop = location.pathname.startsWith('/prodotto/') && queryParams.get('shop');
  // Hide on customer-facing flows when scoped to a shop via ?from=slug (cart/account/tracking/checkout)
  const SHOP_SCOPED_PATHS = ['/account', '/carrello', '/tracking', '/pagamento/successo'];
  const isShopScoped = SHOP_SCOPED_PATHS.some(p => location.pathname.startsWith(p)) && fromShop;
  // Hide platform header entirely when visiting via a custom domain (white-labelled tenant)
  const host = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
  const isPlatformHost =
    ['photovendo.com', 'www.photovendo.com', 'localhost', '127.0.0.1'].includes(host) ||
    host.endsWith('.photovendo.com') ||
    host.endsWith('.emergentagent.com');
  const isCustomDomain = host && !isPlatformHost;
  // White-label custom domain: hide platform header for end customers,
  // but KEEP it visible for shop admins / superadmins on EVERY page (logout, settings, etc.)
  const isStaff = !!(user && (user.role === 'admin' || user.role === 'superadmin'));
  const alwaysHide = HIDE_ON.includes(location.pathname) || location.pathname.startsWith('/apri-negozio');
  const shopFacing = isShopPublic || isProductFromShop || isShopScoped || isCustomDomain;
  const isHidden = alwaysHide || (!isStaff && shopFacing);

  // Universal search effect (debounced) — must be before any early return
  useEffect(() => {
    if (!searchOpen) return;
    if (!searchQ || searchQ.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API}/admin/search`, {
          withCredentials: true,
          params: { q: searchQ.trim(), limit: 8 },
        });
        setSearchResults(data);
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, searchOpen]);

  // Auto-focus search when opened
  useEffect(() => {
    if (searchOpen) setTimeout(() => searchInputRef.current?.focus(), 100);
  }, [searchOpen]);

  if (isHidden) return null;

  let items = [];
  if (isSuperAdmin) {
    items = [
      { path: '/superadmin', icon: LayoutDashboard, label: 'Pannello SuperAdmin' },
      { path: '/superadmin/test-profiles', icon: Sparkles, label: 'Profili Test' },
      { path: '/superadmin/impostazioni', icon: ShieldCheck, label: 'Impostazioni Piattaforma' },
      { path: '/admin', icon: Camera, label: 'Prodotti' },
      { path: '/ordini', icon: Package, label: 'Ordini' },
      { path: '/negozio/impostazioni', icon: Settings, label: 'Negozio' },
      { path: '/account', icon: User, label: 'Account' },
    ];
  } else if (isAdmin) {
    items = [
      { path: '/dashboard', icon: BarChart3, label: 'Dashboard' },
      { path: '/admin', icon: Camera, label: 'Carica prodotti' },
      { path: '/ordini', icon: Package, label: 'Ordini' },
      { path: '/negozio/impostazioni', icon: Settings, label: 'Impostazioni Negozio' },
      { path: '/account', icon: User, label: 'Account' },
    ];
  } else {
    items = [
      { path: '/', icon: Home, label: 'Home' },
      { path: '/catalogo', icon: Grid3X3, label: 'Catalogo' },
      { path: '/carrello', icon: ShoppingCart, label: 'Carrello' },
      { path: '/tracking', icon: Package, label: 'Traccia ordine' },
      { path: '/account', icon: User, label: 'Account' },
    ];
  }

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const doLogout = async () => {
    setOpen(false);
    if (logout) {
      try { await logout(); } catch { /* ignore */ }
    }
    navigate('/');
  };

  const roleLabel = isSuperAdmin ? 'SuperAdmin' : isAdmin ? 'Negoziante' : user ? 'Cliente' : 'Ospite';

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-[150] bg-white/95 backdrop-blur border-b border-[#E8E3D9]"
    >
      <div className="max-w-2xl mx-auto px-3 h-14 flex items-center justify-between">
        <button
          data-testid="header-logo"
          onClick={() => navigate(isSuperAdmin ? '/superadmin' : isAdmin ? '/admin' : '/')}
          className="flex items-center gap-2"
        >
          <div className="w-8 h-8 rounded-lg bg-[#C25934] flex items-center justify-center">
            <Camera size={18} className="text-white" strokeWidth={2.2} />
          </div>
          <div className="text-left">
            <p className="text-[15px] font-bold text-[#2A2624] leading-tight" style={{ fontFamily: 'Outfit' }}>
              PhotoVendo
            </p>
            <p className="text-[10px] text-[#877E78] leading-tight">{roleLabel}</p>
          </div>
        </button>

        <div className="flex items-center gap-1">
          {(isAdmin || isSuperAdmin) && (
            <button
              data-testid="header-search-btn"
              onClick={() => setSearchOpen(true)}
              aria-label="Cerca"
              className="p-2 rounded-xl hover:bg-[#F0EBE1] text-[#2A2624]"
            >
              <Search size={22} strokeWidth={2} />
            </button>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              data-testid="header-hamburger-btn"
              aria-label="Apri menu"
              className="p-2 rounded-xl hover:bg-[#F0EBE1] text-[#2A2624]"
            >
              <Menu size={24} strokeWidth={2} />
            </button>
          </SheetTrigger>          <SheetContent
            side="right"
            data-testid="header-menu-sheet"
            className="w-[82%] sm:w-80 p-0 bg-[#FDFBF7] border-l border-[#E8E3D9]"
          >
            <div className="flex flex-col h-full">
              <div className="px-4 py-4 border-b border-[#E8E3D9] flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
                    Menu
                  </p>
                  <p className="text-xs text-[#877E78] truncate max-w-[220px]">
                    {user?.email || 'Non autenticato'}
                  </p>
                </div>
                <button
                  data-testid="header-menu-close"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-full hover:bg-[#F0EBE1]"
                  aria-label="Chiudi menu"
                >
                  <X size={20} className="text-[#877E78]" />
                </button>
              </div>

              <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                {items.map(({ path, icon: Icon, label }) => {
                  const active = location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
                  return (
                    <button
                      key={path}
                      data-testid={`menu-item-${path.replace(/\//g, '-').replace(/^-/, '') || 'home'}`}
                      onClick={() => go(path)}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors ${
                        active
                          ? 'bg-[#C25934] text-white'
                          : 'text-[#2A2624] hover:bg-[#F0EBE1]'
                      }`}
                    >
                      <Icon size={20} strokeWidth={active ? 2.2 : 1.8} />
                      <span className="text-sm font-medium">{label}</span>
                    </button>
                  );
                })}
              </nav>

              {user && (
                <div className="border-t border-[#E8E3D9] p-3">
                  <button
                    data-testid="menu-logout-btn"
                    onClick={doLogout}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-red-600 hover:bg-red-50"
                  >
                    <LogOut size={20} strokeWidth={1.8} />
                    <span className="text-sm font-medium">Esci</span>
                  </button>
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
        </div>
      </div>

      {/* Universal Search overlay */}
      {searchOpen && (
        <div
          data-testid="universal-search-overlay"
          className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-16 px-4"
          onClick={() => { setSearchOpen(false); setSearchQ(''); setSearchResults(null); }}
        >
          <div
            className="bg-white rounded-2xl border border-[#E8E3D9] shadow-2xl w-full max-w-xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E8E3D9]">
              <Search size={18} className="text-[#877E78] flex-shrink-0" />
              <input
                ref={searchInputRef}
                data-testid="universal-search-input"
                type="search"
                placeholder="Cerca prodotti, ordini, volantini..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                className="flex-1 outline-none bg-transparent text-sm"
              />
              <button
                data-testid="universal-search-close"
                onClick={() => { setSearchOpen(false); setSearchQ(''); setSearchResults(null); }}
                className="p-1 rounded-full hover:bg-[#F0EBE1] text-[#877E78]"
              >
                <X size={18} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {searchQ.trim().length < 2 && (
                <p className="p-6 text-center text-xs text-[#877E78]">
                  Digita almeno 2 caratteri per cercare in tutto il negozio.
                </p>
              )}
              {searching && <p className="p-6 text-center text-xs text-[#877E78]">Cerco...</p>}
              {searchResults && searchResults.total === 0 && !searching && (
                <p className="p-6 text-center text-xs text-[#877E78]">Nessun risultato per "{searchQ}"</p>
              )}
              {searchResults && searchResults.total > 0 && (
                <div className="p-2 space-y-3">
                  {searchResults.products.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-[#877E78] uppercase px-2 py-1">Prodotti ({searchResults.products.length})</p>
                      {searchResults.products.map(p => (
                        <button
                          key={p.id}
                          data-testid={`search-result-product-${p.id}`}
                          onClick={() => {
                            setSearchOpen(false); setSearchQ(''); setSearchResults(null);
                            navigate(`/prodotto/${p.id}`);
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#F0EBE1] text-left"
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#F0EBE1] flex items-center justify-center text-[#877E78] flex-shrink-0">
                            <Eye size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2624] truncate">{p.title}</p>
                            <p className="text-xs text-[#877E78]">€{(p.price || 0).toFixed(2)} · {p.status}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.orders.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-[#877E78] uppercase px-2 py-1">Ordini ({searchResults.orders.length})</p>
                      {searchResults.orders.map(o => (
                        <button
                          key={o.id}
                          data-testid={`search-result-order-${o.id}`}
                          onClick={() => {
                            setSearchOpen(false); setSearchQ(''); setSearchResults(null);
                            navigate(`/ordini`);
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#F0EBE1] text-left"
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#D98324]/10 flex items-center justify-center text-[#D98324] flex-shrink-0">
                            <Package size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2624] truncate">
                              #{(o.id || '').slice(0, 8)} · {o.customer_name || o.customer_email || 'Cliente'}
                            </p>
                            <p className="text-xs text-[#877E78]">€{(o.total || 0).toFixed(2)} · {o.status}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchResults.flyers.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-[#877E78] uppercase px-2 py-1">Volantini ({searchResults.flyers.length})</p>
                      {searchResults.flyers.map(f => (
                        <button
                          key={f.id}
                          data-testid={`search-result-flyer-${f.id}`}
                          onClick={() => {
                            setSearchOpen(false); setSearchQ(''); setSearchResults(null);
                            navigate(`/volantino/${f.id}`);
                          }}
                          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-[#F0EBE1] text-left"
                        >
                          <div className="w-10 h-10 rounded-lg bg-[#C25934]/10 flex items-center justify-center text-[#C25934] flex-shrink-0">
                            📰
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#2A2624] truncate">{f.title}</p>
                            <p className="text-xs text-[#877E78]">{f.date} · {f.product_count} prodotti</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
